import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { Skeleton } from "@mui/material";
import {
  Box,
  Typography,
  Unstable_Grid2 as Grid2,
  ClickAwayListener,
  LinearProgress,
  Grid,
} from "@mui/material";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CircularProgress from "@mui/material/CircularProgress";
import {
  BodyLarge,
  H7TitleLarge,
  H8TitleMedium,
  LabelLarge,
  BodyMedium,
  BodySmall,
  LabelMedium,
} from "../components/StyledComponents/Typography/Typography.tsx";
import {
  FetchDashboardStatisticsCS,
  FetchDashboardStatisticsLEDCS,
  GetIssuesCountForDashboardCS,
  GetAllOrgsSitesList,
  GetUserUseQuery,
} from "../api/api.js";
import { useHistory } from "react-router-dom";
import PageWrapper from "../components/PageWrapper/PageWrapper.js";
import LineChart from "./LineChart/LineChart.js";

import { apiBodyConstructorForDashboardCS, apiBodyConstructorForCurrentStatsCS } from "../api/apiHelpers.js";
import useAppContextProvider from "../AppContext/useAppContextProvider.js";
import { useQuery } from "@tanstack/react-query";
import {
  generateConfigForGraphs,
  generateDataForGraphs,
} from "./utils/utils.js";
import AuradineDataNotFoundState from "../components/StyledComponents/EmptyStates/AuradineDataNotFoundState.tsx";
import AuradineErrorState from "../components/StyledComponents/EmptyStates/AuradineErrorState.tsx";
import { GroupOperationsTab } from "./GroupOperations/GroupOperations.js";
import { capitalizeString } from "../components/util.js";
import {
  StyledMenu,
  StyledMenuItem,
} from "../components/StyledComponents/Menu/Menu.tsx";
import { OutlinedButton } from "../components/StyledComponents/Buttons/AuradineButtons.js";
import { isEmpty } from "lodash";
import { GetUserTechsupport } from "../api/api.js";
import { useTheme } from "../ThemeContext/ThemeContext.js";

export const MetricCard = styled(Paper)(({ theme, colors }) => ({
  backgroundColor: colors?.background?.card || (theme.palette.mode === "dark" ? "#1A2027" : "#fff"),
  border: `1px solid ${colors?.border?.card}`,
  paddingBottom: theme.spacing(4),
  paddingTop: theme.spacing(4),
  borderRadius: "1rem",
  width: "100%",
  height: "100%",
}));

export const ChartCard = styled(Paper)(({ theme, colors }) => ({
  backgroundColor: colors?.background?.card || (theme.palette.mode === "dark" ? "#1A2027" : "#fff"),
  border: `1px solid ${colors?.border?.card}`,
  // paddingLeft: theme.spacing(6),
  // paddingRight: theme.spacing(6),
  paddingBottom: theme.spacing(4),
  paddingTop: theme.spacing(4),
  borderRadius: "1rem",
  textAlign: "center",
  width: "100%",
  height: "100%",
}));
export const MinimizedChartCard = styled(Paper)(({ theme, colors }) => ({
  backgroundColor: colors?.background?.card || (theme.palette.mode === "dark" ? "#1A2027" : "#fff"),
  border: `1px solid ${colors?.border?.card}`,
  // paddingLeft: theme.spacing(6),
  // paddingRight: theme.spacing(6),
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(2),
  borderRadius: "1rem",
  textAlign: "center",
  width: "100%",
  height: "100%",
}));

export const DashboardTitleTypography = styled(Typography)(({ theme, colors }) => ({
  ...theme.typography.h6,
  fontSize: "1.5rem",
  fontWeight: 400,
  color: colors?.text?.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const LineChartTitleTypography = styled(Typography)(({ theme, colors }) => ({
  ...theme.typography.body2,
  fontSize: "1.125rem",
  fontWeight: 400,
  color: colors?.text?.secondary,
}));

export const LoadingSpinner = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.dark,
}));

// LinearProgressWithLabel component
const LinearProgressWithLabel = ({
  value,
  label,
  count,
  total,
  color,
  colors,
}) => {
  const progressColor = color || colors?.primary?.main;  
  
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <LabelMedium style={{ color: colors?.text?.secondary }}>{label}</LabelMedium>
        <Box display="flex" gap={1} alignItems="center">
          <BodySmall style={{ color: colors?.text?.muted }}>
            {count}/{total}
          </BodySmall>
          <BodySmall style={{ color: colors?.text?.muted }}>
            {value.toFixed(1)}%
          </BodySmall>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: colors?.border?.muted,
          "& .MuiLinearProgress-bar": {
            backgroundColor: progressColor,
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
};

const TechSupportHome = () => {
  /*ToDo: We encountered an issue where the navbar designed by Diana has a maximum height of 48px, while the current navbar has a height of 60px. This discrepancy caused the content to overflow and require scrolling when placed as specified in Figma. To address this, we made some adjustments to the dimensions of a few components.
    The filters were given a maximum height of 2rem, and the container housing them was adjusted accordingly.
    The expanded graph, originally intended to have a height of 368px, was reduced to 328px to accommodate all graphs on one page without causing overflow or cutting off content.
    The smaller graphs were kept at the exact heights specified in the design. */

  const {
    selectedValue,
    setSelectedValue,
    utcTimeStartString,
    utcTimeEndString,
    binSize,
    calculateDateTimeValues,
    timeFilterValues: timeRanges,
    // selectedGroup,
    // setSelectedGroup,
  } = useAppContextProvider();

  const history = useHistory();

  const [authToken, setAuthToken] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedGroupName, setSelectedGroupName] = useState("");

  const [isExpandedGraphsModeActivated, setIsExpandedGraphsModeActivated] =
    useState(false);

  const [expandedChartIndex, setExpandedChartIndex] = useState(-1);
  const [expandedChart, setExpandedChart] = useState([]);
  const [otherCharts, setOtherCharts] = useState([]);
  const [validUser, setValidUser] = React.useState(-1);

  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn("User is not authenticated. Fetching token...");
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000);

    // Clean up the interval on unmount
    return () => clearInterval(interval);
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    const fn = async () => {
      const token = await getAccessTokenSilently();
      await GetUserTechsupport(token, (data) => {
        if (data.status === "error") {
          if (data.errorCode === 1007) {
            setValidUser(1);
          } else {
            setValidUser(1);
          }
        } else {
          setValidUser(0);
        }
      });
    };
    fn();
  }, [getAccessTokenSilently]);

  //useEffect to calculate date time values on load of the page
  useEffect(() => {
    document.title = "Home";
    calculateDateTimeValues(selectedValue);
  }, []);

  const handleChange = (event) => {
    calculateDateTimeValues(event.target.value);
  };

  const {
    data: siteLists,
    isLoading: isSiteGroupDataLoading,
    isFetched: isSiteGroupDataFetched,
    isFetching: isSiteGroupDataFetching,
    error: siteGroupDataError,
    refetch: refetchSiteGroupData,
  } = useQuery({
    queryKey: ["orgAndSiteLists"],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return GetAllOrgsSitesList(token);
    },
  });

  // console.log("siteLists", siteLists);

  const {
    data: userInfo,
    isFetched: isUserInfoFetched,
    isFetching: isUserInfoFetching,
  } = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => GetUserUseQuery(authToken),
    enabled: !!authToken,
  });

  let userName = "";

  if (isUserInfoFetched) {
    userName =
      userInfo?.user?.name !== "" ? userInfo?.user?.name : user?.nickname;
  }

  // useQuery to fetch device group data from the server
  const siteOptions =
    siteLists && siteLists?.status === "ok" && siteLists?.siteList?.length > 0
      ? siteLists?.siteList.map((site) => site.siteName)
      : [];

  //api options to fetch dashboard data from the server

  const {
    data: dashboardData,
    isFetched: isDashboardDataFetched,
    isFetching: isDashboardDataFetching,
    isLoading: isDashboardDataLoading,
    isRefetching: isDashboardDataRefetching,
  } = useQuery({
    queryKey: [
      "dashboardData",
      selectedSite,
      selectedGroupName,
      selectedValue,
      utcTimeStartString,
      utcTimeEndString,
      binSize,
    ],
    queryFn: async ({ queryKey }) => {
      const apiOptions = apiBodyConstructorForDashboardCS(
        utcTimeStartString,
        utcTimeEndString,
        binSize,
        authToken,
        selectedGroupName,
        selectedSite,
        selectedOrg
      );

      // Call the API
      const data = await FetchDashboardStatisticsCS(queryKey[0], apiOptions);

      // Wait for 1 second before returning
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return data;
    },
    enabled: !!selectedSite,
  });

  const {
    data: dashboardDataLED,
    isFetched: isDashboardDataFetchedLED,
    isFetching: isDashboardDataFetchingLED,
    isLoading: isDashboardDataLoadingLED,
    isRefetching: isDashboardDataRefetchingLED,
  } = useQuery({
    queryKey: [
      "dashboardDataLED",
      selectedSite,
      selectedGroupName,
      selectedValue,
      utcTimeStartString,
      utcTimeEndString,
      binSize,
    ],
    queryFn: async ({ queryKey }) => {
      const apiOptions = apiBodyConstructorForDashboardCS(
        utcTimeStartString,
        utcTimeEndString,
        binSize,
        authToken,
        selectedGroupName,
        selectedSite,
        selectedOrg
      );

      // Call the API
      const data = await FetchDashboardStatisticsLEDCS(queryKey[0], apiOptions);

      // Wait for 1 second before returning
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return data;
    },
    enabled: !!selectedSite,
  });

  const {
    data: currentIssuesCountForDashboard,
    isFetched: isCurrentIssuesCountForDashboardFetched,
    isFetching: isCurrentIssuesCountForDashboardFetching,
    isLoading: isCurrentIssuesCountForDashboardLoading,
    isRefetching: isCurrentIssuesCountForDashboardRefetching,
    refetch: refetchIssuesCount,
  } = useQuery({
    queryKey: [
      "currentIssuesCountForDashboard",
      selectedOrg,
      selectedGroupName,
      selectedSite,
    ],
    queryFn: async ({ queryKey }) => {
      const token = await getAccessTokenSilently();
      const apiOptions = apiBodyConstructorForCurrentStatsCS(
        token,
        selectedOrg,
        selectedGroupName,
        selectedSite
      );

      const data = await GetIssuesCountForDashboardCS(queryKey[0], apiOptions);

      // Add 1 second delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return data;
    },
    refetchInterval: 60000,
    enabled: !!selectedOrg,
  });

  const totalIssuesCount =
  (currentIssuesCountForDashboard?.msg?.hashboardIssuesCount ?? 0) +
  (currentIssuesCountForDashboard?.msg?.fanIssuesCount ?? 0) +
  (currentIssuesCountForDashboard?.msg?.psuIssuesCount ?? 0) +
  (currentIssuesCountForDashboard?.msg?.leakDetectionCount ?? 0) +
  (currentIssuesCountForDashboard?.msg?.invalidPoolConfigCount ?? 0);

  //generate data for the graphs

  const dataForGraphs = generateDataForGraphs(
    utcTimeStartString,
    utcTimeEndString,
    binSize,
    dashboardData,
    dashboardDataLED
  );

  // generate configurations for the graphs
  const {
    hashRateGraphConfig,
    powerGraphConfig,
    tempuratureGraphConfig,
    uptimeGraphConfig,
    controlBoardTempGraphConfig,
    issuesGraphConfig,
    hasIssuesData,
  } = generateConfigForGraphs(dataForGraphs, binSize);

  // Issues graph is always displayed separately, not part of expansion logic
  const issuesGraphData = {
    heading: "Issues Over Time",
    subHeading: "in count",
    chartOptions: issuesGraphConfig,
    hasIssuesData: hasIssuesData,
    isExpanded: false,
    isExpansionRequired: false,
  };

  let lineChartsData = [
    {
      heading: "Average Hashrate",
      subHeading: "in TH/s",
      chartOptions: hashRateGraphConfig,
      isExpanded: false,
      isExpansionRequired: true,
    },
    {
      heading: "Average Temperature",
      subHeading: "in ˚C",
      chartOptions: tempuratureGraphConfig,
      isExpanded: false,
      isExpansionRequired: true,
    },
    {
      heading: "Power",
      subHeading: "in kW",
      chartOptions: powerGraphConfig,
      isExpanded: false,
      isExpansionRequired: true,
    },
    {
      heading: "Average Control Board Temperature",
      subHeading: "in ˚C",
      chartOptions: controlBoardTempGraphConfig,
      isExpanded: false,
      isExpansionRequired: true,
    },
    // {
    //   heading: "Uptime",
    //   subHeading: "in minutes",
    //   chartOptions: uptimeGraphConfig,
    //   isExpanded: false,
    //   isExpansionRequired: true,
    // },
  ];

  //for the expansion mode, everytime user clicks on the expand button, the graph will be expanded
  // everytime user clicks on expand, we will pick the chart index and push it to expandedChart we form two arrays
  // one for the chart that needs to be expanded and one for all the other charts
  // we will then map over the two arrays and render the expanded chart first and then the other charts
  //on click of expand button we will have to expand the chart so sharing setExpandedChartIndex is sufficient

  useEffect(() => {
    if (expandedChartIndex !== -1) {
      const chartData = lineChartsData[expandedChartIndex];

      setExpandedChart([lineChartsData[expandedChartIndex]]);
      setOtherCharts(
        lineChartsData.filter((_, index) => index !== expandedChartIndex)
      );
      setIsExpandedGraphsModeActivated(true);
    }
    if (expandedChartIndex === -1) {
      setExpandedChart([]);
      setOtherCharts([]);
      setIsExpandedGraphsModeActivated(false);
    }
  }, [expandedChartIndex, dashboardData]);

  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = React.useRef(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClose = (view) => {
    calculateDateTimeValues(view);
    setAnchorEl(null);
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  const setMenuOpen = () => {
    setAnchorEl(buttonRef.current);
  };

  const { colors } = useTheme();

  if (validUser === 0) {
    return (
      <PageWrapper>
        {isUserInfoFetching ? (
          <Skeleton
            variant="rectangular"
            width={"auto"}
            animation={"wave"}
            height={200}
            sx={{
              borderRadius: 2,
              backgroundColor: colors?.background?.secondary,
            }}
          />
        ) : (
          <Box
            className={"header-container"}
            display={"flex"}
            justifyContent="flex-start"
            flexDirection="column"
            gap={4}
            marginBottom={"1.5rem"}
          >
            <Box
              className={"title-box"}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"space-between"}
              gap={3.5}
            >
              <H7TitleLarge>Welcome to FluxVision, {userName}!</H7TitleLarge>
              <BodyLarge>
                Monitor your mining performance for
                organizations, sites, and groups. Select a Site or Group to view
                issue and performance graphs with time-range controls.
              </BodyLarge>
            </Box>

            <Box
              className={"filter-box"}
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
              alignItems={"center"}
              // marginRight={"1.5rem"}
              flex={1}
              gap={2}
              maxHeight={"2rem"}
            >
              <H8TitleMedium> View data for: </H8TitleMedium>
              <GroupOperationsTab
                selectedOrg={selectedOrg}
                setSelectedOrg={setSelectedOrg}
                selectedSite={selectedSite}
                setSelectedSite={setSelectedSite}
                selectedGroup={selectedGroupName}
                setSelectedGroup={setSelectedGroupName}
                user={userInfo?.user}
                siteLists={siteLists}
                isDashboardDataFetching={isDashboardDataFetching}
                isDashboardDataLoading={isDashboardDataLoading}
                isDashboardDataRefetching={isDashboardDataRefetching}
              />
              <ClickAwayListener onClickAway={handleClickAway}>
                <>
                  <OutlinedButton
                    colors={colors}
                    ref={buttonRef}
                    onClick={setMenuOpen}
                    endIcon={
                      Boolean(anchorEl) ? (
                        <KeyboardArrowUpRoundedIcon />
                      ) : (
                        <KeyboardArrowDownRoundedIcon />
                      )
                    }
                    disabled={
                      isDashboardDataFetching ||
                      isDashboardDataLoading ||
                      isDashboardDataRefetching
                    }
                  >
                    {capitalizeString(selectedValue)}
                  </OutlinedButton>
                  <StyledMenu   
                    colors={colors}
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClickAway}
                  >
                    {timeRanges.map((calendarView, index) => (
                      <StyledMenuItem
                        colors={colors}
                        value={calendarView.value}
                        key={`menuItem${index}`}
                        selected={calendarView.value === selectedValue}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleMenuClose(calendarView.value);
                        }}
                        sx={{ minWidth: "7rem" }}
                      >
                        {capitalizeString(calendarView.label)}
                      </StyledMenuItem>
                    ))}
                  </StyledMenu>
                </>
              </ClickAwayListener>
            </Box>
          </Box>
        )}
        <Grid
          container
          className={"home-page-container"}
          display={"flex"}
          flexDirection={"row"}
          sx={{ transition: "all 0.3s ease-in-out" }}
        >
          <Box
            width="100%"
            py={4}
            display="flex"
            flexDirection="column"
            gap={6}
            sx={{
              transition: "width 0.3s ease-in-out",
              borderRadius: "1rem 1rem 1rem 1rem",
            }}
          >
            {selectedOrg && 
            <Box display="flex" flex-direction={"row"} gap={4}>
              <MetricCard elevation={0} colors={colors}>
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  padding={"0.675rem 1.5rem"}
                  sx={{ height: "100%" }}
                  gap={6}
                >
                  <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <LabelLarge>Issue Breakdown</LabelLarge>
                    <BodyMedium style={{ color: colors?.text?.muted }}>
                      Total Issues: {totalIssuesCount || 0}
                    </BodyMedium>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    sx={{ flex: 1 }}
                  >
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg
                            ?.hashboardIssuesCount
                            ? (currentIssuesCountForDashboard.msg
                                .hashboardIssuesCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Hash board malfunction",
                              });
                              if (selectedOrg)
                                params.append("org_name", selectedOrg);
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/techsupportminerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFiltersCS",
                                  "siteGroupCS",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = {
                                  status: "Hash board malfunction",
                                };
                                const newSiteGroupFilters =
                                selectedOrg && selectedSite
                                  ? {
                                      org_name: selectedOrg,
                                      siteName: selectedSite,
                                      ...(selectedGroupName ? { dgname: selectedGroupName } : {}),
                                    }
                                  : null;
                                sessionStorage.setItem(
                                  "minerFiltersCS",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroupCS",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Hashboard Malfunction
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg
                            ?.hashboardIssuesCount || 0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg?.psuIssuesCount
                            ? (currentIssuesCountForDashboard.msg
                                .psuIssuesCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "PSU malfunction",
                              });
                              if (selectedOrg)
                                params.append("org_name", selectedOrg);
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/techsupportminerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFiltersCS",
                                  "siteGroupCS",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = {
                                  status: "PSU malfunction",
                                };
                                const newSiteGroupFilters =
                                selectedOrg && selectedSite
                                  ? {
                                      org_name: selectedOrg,
                                      siteName: selectedSite,
                                      ...(selectedGroupName ? { dgname: selectedGroupName } : {}),
                                    }
                                  : null;
                                sessionStorage.setItem(
                                  "minerFiltersCS",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroupCS",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            PSU Malfunction
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg?.psuIssuesCount ||
                          0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg?.fanIssuesCount
                            ? (currentIssuesCountForDashboard.msg
                                .fanIssuesCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Fan malfunction",
                              });
                              if (selectedOrg)
                                params.append("org_name", selectedOrg);
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/techsupportminerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFiltersCS",
                                  "siteGroupCS",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = {
                                  status: "Fan malfunction",
                                };
                                const newSiteGroupFilters =
                                selectedOrg && selectedSite
                                  ? {
                                      org_name: selectedOrg,
                                      siteName: selectedSite,
                                      ...(selectedGroupName ? { dgname: selectedGroupName } : {}),
                                    }
                                  : null;
                                sessionStorage.setItem(
                                  "minerFiltersCS",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroupCS",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Fan Malfunction
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg?.fanIssuesCount ||
                          0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg
                            ?.invalidPoolConfigCount
                            ? (currentIssuesCountForDashboard.msg
                                .invalidPoolConfigCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Pool configuration invalid",
                              });
                              if (selectedOrg)
                                params.append("org_name", selectedOrg);
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/techsupportminerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFiltersCS",
                                  "siteGroupCS",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = {
                                  status: "Pool configuration invalid",
                                };
                                const newSiteGroupFilters =
                                selectedOrg && selectedSite
                                  ? {
                                      org_name: selectedOrg,
                                      siteName: selectedSite,
                                      ...(selectedGroupName ? { dgname: selectedGroupName } : {}),
                                    }
                                  : null;
                                sessionStorage.setItem(
                                  "minerFiltersCS",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroupCS",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Invalid Pool Config
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg
                            ?.invalidPoolConfigCount || 0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <LinearProgressWithLabel
                      value={
                        currentIssuesCountForDashboard?.msg?.leakDetectionCount
                          ? (currentIssuesCountForDashboard.msg
                              .leakDetectionCount /
                              (currentIssuesCountForDashboard?.msg
                                ?.totalCount || 1)) *
                            100
                          : 0
                      }
                      colors={colors}
                      label={
                        <Box
                          component="span"
                          sx={{
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                          onClick={() => {
                            const params = new URLSearchParams({
                              status: "Leak Detected",
                            });
                            if (selectedOrg)
                              params.append("org_name", selectedOrg);
                            if (selectedSite)
                              params.append("siteName", selectedSite);
                            if (selectedGroupName)
                              params.append("dgname", selectedGroupName);
                            history.push(`/techsupportminerstatus?${params.toString()}`);
                            try {
                              const storageToClear = [
                                "minerFiltersCS",
                                "siteGroupCS",
                              ];
                              storageToClear.forEach((key) =>
                                sessionStorage.removeItem(key)
                              );
                              const newMinerFilters = {
                                status: "Leak Detected",
                              };
                              const newSiteGroupFilters =
                                selectedOrg && selectedSite
                                  ? {
                                      org_name: selectedOrg,
                                      siteName: selectedSite,
                                      ...(selectedGroupName ? { dgname: selectedGroupName } : {}),
                                    }
                                  : null;
                              sessionStorage.setItem(
                                "minerFiltersCS",
                                JSON.stringify(newMinerFilters)
                              );
                              sessionStorage.setItem(
                                "siteGroupCS",
                                JSON.stringify(newSiteGroupFilters)
                              );
                            } catch (e) {
                              console.error(
                                "Failed to update sessionStorage:",
                                e
                              );
                            }
                          }}
                        >
                          Leak Detected
                        </Box>
                      }
                      count={
                        currentIssuesCountForDashboard?.msg
                          ?.leakDetectionCount || 0
                      }
                      total={
                        currentIssuesCountForDashboard?.msg?.totalCount || 0
                      }
                    />
                  </Box>
                </Box>
              </MetricCard>
              <MetricCard elevation={0} colors={colors}>
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  padding={"0.675rem 1.5rem"}
                  sx={{ height: "100%" }}
                  gap={6}
                >
                  <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <LabelLarge>Miner Status Distribution</LabelLarge>
                    <BodyMedium style={{ color: colors?.text?.muted }}>
                      Total Miners:{" "}
                      {currentIssuesCountForDashboard?.msg?.totalCount || 0}
                    </BodyMedium>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    sx={{ flex: 1 }}
                  >
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg?.connectedCount
                            ? (currentIssuesCountForDashboard.msg
                                .connectedCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Connected",
                              });
                              if (selectedOrg)
                                params.append("org_name", selectedOrg);
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/techsupportminerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFiltersCS",
                                  "siteGroupCS",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = { status: "Connected" };
                                const newSiteGroupFilters =
                                selectedOrg && selectedSite
                                  ? {
                                      org_name: selectedOrg,
                                      siteName: selectedSite,
                                      ...(selectedGroupName ? { dgname: selectedGroupName } : {}),
                                    }
                                  : null;
                                sessionStorage.setItem(
                                  "minerFiltersCS",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroupCS",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Connected
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg?.connectedCount ||
                          0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg?.onboardedCount
                            ? (currentIssuesCountForDashboard.msg
                                .onboardedCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Onboarded",
                              });
                              if (selectedOrg)
                                params.append("org_name", selectedOrg);
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/techsupportminerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFiltersCS",
                                  "siteGroupCS",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = { status: "Onboarded" };
                                const newSiteGroupFilters =
                                selectedOrg && selectedSite
                                  ? {
                                      org_name: selectedOrg,
                                      siteName: selectedSite,
                                      ...(selectedGroupName ? { dgname: selectedGroupName } : {}),
                                    }
                                  : null;
                                sessionStorage.setItem(
                                  "minerFiltersCS",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroupCS",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Onboarded
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg?.onboardedCount ||
                          0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg?.standbyCount
                            ? (currentIssuesCountForDashboard.msg.standbyCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Standby Mode",
                              });
                              if (selectedOrg)
                                params.append("org_name", selectedOrg);
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/techsupportminerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFiltersCS",
                                  "siteGroupCS",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = {
                                  status: "Standby Mode",
                                };
                                const newSiteGroupFilters =
                                selectedOrg && selectedSite
                                  ? {
                                      org_name: selectedOrg,
                                      siteName: selectedSite,
                                      ...(selectedGroupName ? { dgname: selectedGroupName } : {}),
                                    }
                                  : null;
                                sessionStorage.setItem(
                                  "minerFiltersCS",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroupCS",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Standby Mode
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg?.standbyCount || 0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <LinearProgressWithLabel
                      value={
                        currentIssuesCountForDashboard?.msg?.disconnectedCount
                          ? (currentIssuesCountForDashboard.msg
                              .disconnectedCount /
                              (currentIssuesCountForDashboard?.msg
                                ?.totalCount || 1)) *
                            100
                          : 0
                      }
                      colors={colors}
                      label={
                        <Box
                          component="span"
                          sx={{
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                          onClick={() => {
                            const params = new URLSearchParams({
                              status: "Disconnected",
                            });
                            if (selectedOrg)
                              params.append("org_name", selectedOrg);
                            if (selectedSite)
                              params.append("siteName", selectedSite);
                            if (selectedGroupName)
                              params.append("dgname", selectedGroupName);
                            history.push(`/techsupportminerstatus?${params.toString()}`);
                            try {
                              const storageToClear = [
                                "minerFiltersCS",
                                "siteGroupCS",
                              ];
                              storageToClear.forEach((key) =>
                                sessionStorage.removeItem(key)
                              );
                              const newMinerFilters = {
                                status: "Disconnected",
                              };
                              const newSiteGroupFilters =
                                selectedOrg && selectedSite
                                  ? {
                                      org_name: selectedOrg,
                                      siteName: selectedSite,
                                      ...(selectedGroupName ? { dgname: selectedGroupName } : {}),
                                    }
                                  : null;
                              sessionStorage.setItem(
                                "minerFiltersCS",
                                JSON.stringify(newMinerFilters)
                              );
                              sessionStorage.setItem(
                                "siteGroupCS",
                                JSON.stringify(newSiteGroupFilters)
                              );
                            } catch (e) {
                              console.error(
                                "Failed to update sessionStorage:",
                                e
                              );
                            }
                          }}
                        >
                          Disconnected
                        </Box>
                      }
                      count={
                        currentIssuesCountForDashboard?.msg
                          ?.disconnectedCount || 0
                      }
                      total={
                        currentIssuesCountForDashboard?.msg?.totalCount || 0
                      }
                    />
                  </Box>
                </Box>
              </MetricCard>
            </Box>
            }
            <Box width={"100%"}>
              <Grid2
                container
                spacing={isExpandedGraphsModeActivated ? 6 : 6}
                justifyContent="center"
                alignItems="center"
                padding={0}
              >
                {(!isEmpty(siteLists) && siteLists?.status === "error") ||
                (!isEmpty(siteLists) &&
                  siteLists?.status === "ok" &&
                  Array.isArray(siteLists?.orgList) &&
                  siteLists?.orgList.length === 0) ? (
                  <Grid2 item xs={12}>
                    <AuradineErrorState
                      message={
                        siteLists.msg
                          ? `  ${siteLists?.msg}. Please contact your superadmin`
                          : `You may not have permissions to any sites. Please contact superadmin`
                      }
                      fontSize="1.125rem"
                    />
                  </Grid2>
                ) : (
                  <>
                    {isDashboardDataFetched &&
                      !(
                        (dashboardData?.status?.toLowerCase() === "ok" &&
                          dashboardData?.msg?.length > 0) ||
                        (dashboardDataLED?.status?.toLowerCase() === "ok" &&
                          Array.isArray(dashboardDataLED?.msg) &&
                          dashboardDataLED?.msg?.length > 0)
                      ) && (
                        <Grid2 item xs={12}>
                          <AuradineDataNotFoundState
                            message={
                              selectedSite !== "" && selectedGroupName === ""
                                ? `No data found for ${selectedSite} !!`
                                : selectedSite !== "" &&
                                  selectedGroupName !== ""
                                ? `No data found for site ${selectedSite} - group ${selectedGroupName} !!`
                                : `No Site or Group selected`
                            }
                          />
                        </Grid2>
                      )}
                    {isDashboardDataFetched &&
                      dashboardData?.status?.toLowerCase() === "error" &&
                      dashboardDataLED?.status?.toLowerCase() === "error" && (
                        <Grid2 item xs={12}>
                          <AuradineErrorState
                            message={`${dashboardData?.msg}`}
                          />
                        </Grid2>
                      )}
                    {!isExpandedGraphsModeActivated && (
                      <>
                        {/* Issues graph always at full width */}
                        {isDashboardDataFetched &&
                          ((dashboardData?.status?.toLowerCase() === "ok" &&
                            dashboardData?.msg?.length > 0) ||
                            (dashboardDataLED?.status?.toLowerCase() === "ok" &&
                              Array.isArray(dashboardDataLED?.msg) &&
                              dashboardDataLED?.msg?.length > 0)) && (
                            <Grid2 item xs={12}>
                              <ChartCard elevation={0} colors={colors}>
                                <LineChart
                                  key="issues"
                                  chartIndex={-1}
                                  heading={issuesGraphData.heading}
                                  subHeading={issuesGraphData.subHeading}
                                  lineChartOptions={
                                    issuesGraphData.chartOptions
                                  }
                                  hasIssuesData={issuesGraphData.hasIssuesData}
                                  isExpanded={false}
                                  isExpansionRequired={false}
                                />
                              </ChartCard>
                            </Grid2>
                          )}
                        {/* Performance graphs in 2x2 grid with expansion */}
                        {isDashboardDataFetched &&
                          ((dashboardData?.status?.toLowerCase() === "ok" &&
                            dashboardData?.msg?.length > 0) ||
                            (dashboardDataLED?.status?.toLowerCase() === "ok" &&
                              Array.isArray(dashboardDataLED?.msg) &&
                              dashboardDataLED?.msg?.length > 0)) &&
                          lineChartsData?.map((chartData, index) => {
                            return (
                              <Grid2 item xs={6} key={index}>
                                <ChartCard elevation={0} colors={colors}>
                                  <LineChart
                                    key={index}
                                    chartIndex={index}
                                    heading={chartData.heading}
                                    subHeading={chartData.subHeading}
                                    lineChartOptions={chartData.chartOptions}
                                    isExpanded={chartData.isExpanded}
                                    isExpansionRequired={
                                      chartData.isExpansionRequired
                                    }
                                    setExpandedChartIndex={
                                      setExpandedChartIndex
                                    }
                                    isExpandedGraphsModeActivated={
                                      isExpandedGraphsModeActivated
                                    }
                                  />
                                </ChartCard>
                              </Grid2>
                            );
                          })}
                        {isDashboardDataFetching && !isDashboardDataFetched && (
                          <>
                            {/* Issues graph skeleton */}
                            <Grid2 item xs={12}>
                              <Skeleton
                                variant="rectangular"
                                width={"auto"}
                                animation={"wave"}
                                height={300}
                                sx={{
                                  borderRadius: 2,
                                  backgroundColor: colors?.background?.secondary,
                                }}
                              />
                            </Grid2>
                            {/* Performance graph skeletons */}
                            {Array(lineChartsData.length)
                              .fill()
                              .map((_, index) => (
                                <Grid2 item xs={6} key={index}>
                                  <Skeleton
                                    key={index}
                                    variant="rectangular"
                                    width={"auto"}
                                    animation={"wave"}
                                    height={300}
                                    sx={{
                                      borderRadius: 2,
                                      backgroundColor: colors?.background?.secondary,
                                    }}
                                  />
                                </Grid2>
                              ))}
                          </>
                        )}
                      </>
                    )}
                    {isExpandedGraphsModeActivated && (
                      <>
                        {/* Issues graph always visible */}
                        {isDashboardDataFetched &&
                          ((dashboardData?.status?.toLowerCase() === "ok" &&
                            dashboardData?.msg?.length > 0) ||
                            (dashboardDataLED?.status?.toLowerCase() === "ok" &&
                              Array.isArray(dashboardDataLED?.msg) &&
                              dashboardDataLED?.msg?.length > 0)) && (
                            <Grid2 item xs={12}>
                              <ChartCard elevation={0} colors={colors}>
                                <LineChart
                                  key="issues"
                                  chartIndex={-1}
                                  heading={issuesGraphData.heading}
                                  subHeading={issuesGraphData.subHeading}
                                  lineChartOptions={
                                    issuesGraphData.chartOptions
                                  }
                                  hasIssuesData={issuesGraphData.hasIssuesData}
                                  isExpanded={false}
                                  isExpansionRequired={false}
                                />
                              </ChartCard>
                            </Grid2>
                          )}
                        {expandedChart.map((chartData, index) => {
                          return (
                            <>
                              {isDashboardDataFetched &&
                                !isDashboardDataFetching &&
                                ((dashboardData?.status?.toLowerCase() ===
                                  "ok" &&
                                  dashboardData?.msg?.length > 0) ||
                                  (dashboardDataLED?.status?.toLowerCase() ===
                                    "ok" &&
                                    Array.isArray(dashboardDataLED?.msg) &&
                                    dashboardDataLED?.msg?.length > 0)) && (
                                  <Grid2 item xs={12} key={index}>
                                    <ChartCard elevation={0} colors={colors}>
                                      <LineChart
                                        key={index}
                                        chartIndex={lineChartsData.findIndex(
                                          (chart) =>
                                            chart.heading === chartData.heading
                                        )}
                                        heading={chartData.heading}
                                        subHeading={chartData.subHeading}
                                        lineChartOptions={
                                          chartData.chartOptions
                                        }
                                        isExpanded={true}
                                        isExpansionRequired={true}
                                        setExpandedChartIndex={
                                          setExpandedChartIndex
                                        }
                                        expandedChartIndex={expandedChartIndex}
                                        isExpandedGraphsModeActivated={
                                          isExpandedGraphsModeActivated
                                        }
                                      />
                                    </ChartCard>
                                  </Grid2>
                                )}
                              {isDashboardDataFetching &&
                                !isDashboardDataFetched && (
                                  <>
                                    <Grid2 item xs={12}>
                                      <Skeleton
                                        variant="rectangular"
                                        width={"auto"}
                                        animation={"wave"}
                                        height={300}
                                        sx={{
                                          borderRadius: 2,
                                          backgroundColor: colors.background.secondary,
                                        }}
                                      />
                                    </Grid2>
                                    <Grid2 item xs={12}>
                                      <Skeleton
                                        variant="rectangular"
                                        width={"auto"}
                                        animation={"wave"}
                                        height={300}
                                        sx={{
                                          borderRadius: 2,
                                          backgroundColor: colors.background.secondary,
                                        }}
                                      />
                                    </Grid2>
                                    {Array(3)
                                      .fill()
                                      .map((_, index) => (
                                        <Grid2 item xs={2.666} key={index}>
                                          <Skeleton
                                            variant="rectangular"
                                            width={"auto"}
                                            animation={"wave"}
                                            height={200}
                                            sx={{
                                              borderRadius: 2,
                                              backgroundColor: colors.background.secondary,
                                            }}
                                          />
                                        </Grid2>
                                      ))}
                                  </>
                                )}
                            </>
                          );
                        })}
                        {otherCharts.map((chartData, index) => {
                          return (
                            <>
                              {isDashboardDataFetched &&
                                !isDashboardDataFetching &&
                                ((dashboardData?.status?.toLowerCase() ===
                                  "ok" &&
                                  dashboardData?.msg?.length > 0) ||
                                  (dashboardDataLED?.status?.toLowerCase() ===
                                    "ok" &&
                                    Array.isArray(dashboardDataLED?.msg) &&
                                    dashboardDataLED?.msg?.length > 0)) && (
                                  <Grid2 item xs={2.666} key={index}>
                                    <MinimizedChartCard
                                      elevation={0}
                                      colors={colors}
                                      key={index}
                                    >
                                      <LineChart
                                        key={index}
                                        chartIndex={lineChartsData.findIndex(
                                          (chart) =>
                                            chart.heading === chartData.heading
                                        )}
                                        heading={chartData.heading}
                                        subHeading={chartData.subHeading}
                                        lineChartOptions={
                                          chartData.chartOptions
                                        }
                                        isExpanded={false}
                                        isExpansionRequired={true}
                                        setExpandedChartIndex={
                                          setExpandedChartIndex
                                        }
                                        expandedChartIndex={expandedChartIndex}
                                        isExpandedGraphsModeActivated={
                                          isExpandedGraphsModeActivated
                                        }
                                      />
                                    </MinimizedChartCard>
                                  </Grid2>
                                )}
                              {isDashboardDataFetching &&
                                !isDashboardDataFetched && (
                                  <Grid2 item xs={3}>
                                    <Skeleton
                                      variant="rectangular"
                                      width={"auto"}
                                      animation={"wave"}
                                      height={200}
                                      sx={{
                                        borderRadius: 2,
                                        backgroundColor: colors.background.secondary,
                                      }}
                                    />
                                  </Grid2>
                                )}
                            </>
                          );
                        })}
                      </>
                    )}
                  </>
                )}
              </Grid2>
              <Box height="1.5rem" />
            </Box>
          </Box>
        </Grid>
      </PageWrapper>
    );
  } else {
    return <PageWrapper />;
  }
};

export default TechSupportHome;
